import { useState, useEffect, useCallback } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import {
  ShieldAlert,
  UserX,
  Search,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export default function AbuseReports() {
  const [activeTab, setActiveTab] = useState("awaiting");
  const [reports, setReports] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Moderation Modal State
  const [selectedReport, setSelectedReport] = useState(null);
  const [modStatus, setModStatus] = useState("Accepted");
  const [modAction, setModAction] = useState("none");
  const [savingMod, setSavingMod] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const fetchReports = useCallback(async (targetStatus, currentPage = 1, searchQuery = search) => {
    try {
      setLoading(true);
      const statusMap = {
        awaiting: "Awaiting",
        accepted: "Accepted",
        rejected: "Rejected",
      };
      const statusFilter = statusMap[targetStatus];

      const res = await API.get("/admin/reports", {
        params: {
          status: statusFilter,
          search: searchQuery,
          page: currentPage,
          limit: 15,
        },
      });

      if (res.data && res.data.success) {
        setReports(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalCount(res.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error("Error fetching admin reports:", err);
      setFeedback({ type: "error", message: err.response?.data?.message || "Failed to load reports" });
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchBlocks = useCallback(async (currentPage = 1, searchQuery = search) => {
    try {
      setLoading(true);
      const res = await API.get("/admin/blocks", {
        params: {
          search: searchQuery,
          page: currentPage,
          limit: 15,
        },
      });

      if (res.data && res.data.success) {
        setBlocks(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalCount(res.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error("Error fetching admin blocks:", err);
      setFeedback({ type: "error", message: err.response?.data?.message || "Failed to load blocks" });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
    if (activeTab === "blocks") {
      fetchBlocks(1, search);
    } else {
      fetchReports(activeTab, 1, search);
    }
  }, [activeTab, search, fetchReports, fetchBlocks]);

  const currentList = activeTab === "blocks" ? blocks : reports;

  const handleToggleSelectAll = () => {
    if (selectedIds.length === currentList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(currentList.map((item) => item._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const entity = activeTab === "blocks" ? "blocks" : "reports";
      const { data } = await API.post("/admin/batch-delete", {
        entity,
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setFeedback({ type: "success", message: `Deleted ${data.deletedCount || selectedIds.length} ${entity} successfully` });
        handleRefresh();
      }
    } catch (err) {
      setFeedback({ type: "error", message: err?.response?.data?.message || err.message || "Batch delete failed" });
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleRefresh = () => {
    setSelectedIds([]);
    if (activeTab === "blocks") {
      fetchBlocks(page, search);
    } else {
      fetchReports(activeTab, page, search);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    if (activeTab === "blocks") {
      fetchBlocks(1, search);
    } else {
      fetchReports(activeTab, 1, search);
    }
  };

  const openModerateModal = (report) => {
    setSelectedReport(report);
    setModStatus(report.status === "Awaiting" ? "Accepted" : report.status);
    setModAction(report.actionTaken || "none");
  };

  const handleSaveModeration = async () => {
    if (!selectedReport) return;
    try {
      setSavingMod(true);
      const res = await API.patch(`/admin/reports/${selectedReport._id}/status`, {
        status: modStatus,
        actionTaken: modAction,
      });

      if (res.data && res.data.success) {
        setFeedback({ type: "success", message: `Report updated to ${modStatus}` });
        setSelectedReport(null);
        fetchReports(activeTab, page, search);
      }
    } catch (err) {
      console.error("Error updating report status:", err);
      setFeedback({ type: "error", message: err.response?.data?.message || "Failed to update report" });
    } finally {
      setSavingMod(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report record?")) return;
    try {
      const res = await API.delete(`/admin/reports/${reportId}`);
      if (res.data && res.data.success) {
        setFeedback({ type: "success", message: "Report deleted successfully" });
        fetchReports(activeTab, page, search);
      }
    } catch (err) {
      console.error("Error deleting report:", err);
      setFeedback({ type: "error", message: err.response?.data?.message || "Failed to delete report" });
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm("Are you sure you want to remove this user block relationship?")) return;
    try {
      const res = await API.delete(`/admin/blocks/${blockId}`);
      if (res.data && res.data.success) {
        setFeedback({ type: "success", message: "User block removed successfully" });
        fetchBlocks(page, search);
      }
    } catch (err) {
      console.error("Error deleting block:", err);
      setFeedback({ type: "error", message: err.response?.data?.message || "Failed to remove block" });
    }
  };

  const tabs = [
    { id: "awaiting", label: "Awaiting Reports", icon: AlertTriangle },
    { id: "accepted", label: "Accepted Reports", icon: CheckCircle },
    { id: "rejected", label: "Rejected Reports", icon: XCircle },
    { id: "blocks", label: "User Blocks Monitoring", icon: UserX },
  ];

  return (
    <div className="space-y-6 pb-12 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-pink-500" />
            Abuse Reports & User Blocks
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor reported user violations, take disciplinary actions, and inspect platform user-to-user blocks.
          </p>
        </div>

        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors shadow-sm self-start sm:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Feedback Toast Banner */}
      {feedback.message && (
        <div
          className={`p-4 rounded-xl flex items-center justify-between text-sm ${
            feedback.type === "error"
              ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800"
              : "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800"
          }`}
        >
          <span>{feedback.message}</span>
          <button
            onClick={() => setFeedback({ type: "", message: "" })}
            className="font-bold underline text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-slate-700">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all relative ${
                isActive
                  ? "text-pink-600 dark:text-pink-400 font-semibold"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-pink-500" : ""}`} />
              {tab.label}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-rose-500 rounded-t-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4">
        <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-80">
          <input
            type="text"
            placeholder={activeTab === "blocks" ? "Search blocker or blocked user..." : "Search user or report reason..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
        </form>

        <div className="text-xs text-gray-500 dark:text-gray-400">
          Showing <span className="font-semibold text-gray-700 dark:text-gray-300">{totalCount}</span> entries
        </div>
      </div>

      {/* Main Table Content */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-slate-700">
        {loading ? (
          <div className="p-12 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin text-pink-500 mb-3" />
            <p className="text-sm font-medium">Loading {activeTab === "blocks" ? "blocks data" : "reports"}...</p>
          </div>
        ) : activeTab === "blocks" ? (
          /* User Blocks Monitoring Table */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
                  <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                    <input
                      type="checkbox"
                      checked={blocks.length > 0 && selectedIds.length === blocks.length}
                      onChange={handleToggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Blocker User
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Blocked User
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Reason / Note
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Blocked Date
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {blocks.length > 0 ? (
                  blocks.map((block) => {
                    const isSelected = selectedIds.includes(block._id);
                    return (
                    <tr key={block._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                      <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(block._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={block.blocker?.mainImage || "/avatar-placeholder.png"}
                            alt={block.blocker?.name || "User"}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }}
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                              {block.blocker?.name || "Unknown"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              @{block.blocker?.username || "user"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={block.blockedUser?.mainImage || "/avatar-placeholder.png"}
                            alt={block.blockedUser?.name || "User"}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }}
                          />
                          <div>
                            <p className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
                              {block.blockedUser?.name || "Unknown"}
                              {block.blockedUser?.isBlocked && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-300 rounded">
                                  Banned
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              @{block.blockedUser?.username || "user"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-600 dark:text-gray-300">
                        {block.reason || <span className="italic text-gray-400">No reason specified</span>}
                      </td>
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {new Date(block.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-center">
                        <button
                          onClick={() => handleDeleteBlock(block._id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Remove block relationship"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="p-12 text-center text-gray-500 dark:text-gray-400">
                      No block records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* Abuse Reports Table */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
                  <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                    <input
                      type="checkbox"
                      checked={reports.length > 0 && selectedIds.length === reports.length}
                      onChange={handleToggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Reported User
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Reporter
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Reason & Description
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Total Reports
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status / Action
                  </th>
                  <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {reports.length > 0 ? (
                  reports.map((report) => {
                    const isSelected = selectedIds.includes(report._id);
                    return (
                    <tr key={report._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                      <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(report._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                        />
                      </td>

                      {/* Reported User */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={report.reportedUser?.mainImage || "/avatar-placeholder.png"}
                            alt={report.reportedUser?.name || "User"}
                            className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-slate-600"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }}
                          />
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                                {report.reportedUser?.name || "Unknown"}
                              </span>
                              {report.reportedUser?.isBlocked && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-300 rounded font-medium">
                                  Banned
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              @{report.reportedUser?.username || "user"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Reporter */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                            {report.reporter?.name || report.reporter?.username || "Anonymous"}
                          </span>
                        </div>
                      </td>

                      {/* Reason & Description */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 max-w-xs">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 mb-1">
                          {report.reason}
                        </span>
                        {report.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {report.description}
                          </p>
                        )}
                      </td>

                      {/* Created Date */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {new Date(report.createdAt).toLocaleDateString("en-US", {
                          weekday: "short",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>

                      {/* Total Reports For User */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm font-medium text-gray-800 dark:text-gray-200">
                        <span className="px-2.5 py-1 bg-gray-100 dark:bg-slate-700 rounded-lg text-xs font-bold">
                          {report.totalReportForUser || 1}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`px-3 py-1 text-xs font-semibold rounded-full w-fit ${
                              report.status === "Awaiting"
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                                : report.status === "Accepted"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                : "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400"
                            }`}
                          >
                            {report.status}
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openModerateModal(report)}
                            className="px-3 py-1.5 bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/50 rounded-lg text-xs font-semibold transition-colors"
                          >
                            Moderate
                          </button>
                          <button
                            onClick={() => handleDeleteReport(report._id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete Report"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="p-12 text-center text-gray-500 dark:text-gray-400">
                      No reports found for this status.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700/50 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
            <button
              disabled={page <= 1}
              onClick={() => {
                const newP = Math.max(1, page - 1);
                setPage(newP);
                if (activeTab === "blocks") fetchBlocks(newP, search);
                else fetchReports(activeTab, newP, search);
              }}
              className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg disabled:opacity-50 text-gray-700 dark:text-gray-300 flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => {
                const newP = Math.min(totalPages, page + 1);
                setPage(newP);
                if (activeTab === "blocks") fetchBlocks(newP, search);
                else fetchReports(activeTab, newP, search);
              }}
              className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg disabled:opacity-50 text-gray-700 dark:text-gray-300 flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={totalCount}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName={activeTab === "blocks" ? "blocks" : "reports"}
      />

      {/* Moderation Modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-200 dark:border-slate-700 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 pb-3">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" /> Moderate Abuse Report
              </h3>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Report Details */}
            <div className="bg-gray-50 dark:bg-slate-700/50 p-4 rounded-xl space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Reported User:</span>
                <span className="font-semibold text-pink-600 dark:text-pink-400">
                  {selectedReport.reportedUser?.name} (@{selectedReport.reportedUser?.username})
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Reporter:</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {selectedReport.reporter?.name || selectedReport.reporter?.username || "Anonymous"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Reason:</span>
                <span className="font-semibold text-rose-600 dark:text-rose-400">{selectedReport.reason}</span>
              </div>
              {selectedReport.description && (
                <div className="pt-2 border-t border-gray-200 dark:border-slate-600">
                  <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">User Details/Notes:</span>
                  <p className="text-gray-700 dark:text-gray-200 text-xs italic bg-white dark:bg-slate-800 p-2 rounded border border-gray-200 dark:border-slate-600">
                    "{selectedReport.description}"
                  </p>
                </div>
              )}
            </div>

            {/* Moderation Controls */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Report Status
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {["Awaiting", "Accepted", "Rejected"].map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => setModStatus(st)}
                      className={`py-2 text-xs font-semibold rounded-lg border transition-all ${
                        modStatus === st
                          ? st === "Accepted"
                            ? "bg-emerald-500 text-white border-emerald-500"
                            : st === "Rejected"
                            ? "bg-red-500 text-white border-red-500"
                            : "bg-amber-500 text-white border-amber-500"
                          : "bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600"
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Administrative Action on User
                </label>
                <select
                  value={modAction}
                  onChange={(e) => setModAction(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="none">No Action Needed</option>
                  <option value="warned">Issue Warning</option>
                  <option value="banned">Ban User Platform-Wide</option>
                </select>
                {modAction === "banned" && (
                  <p className="text-[11px] text-red-500 font-medium mt-1">
                    ⚠️ Banning this user will prevent them from logging in and accessing app features.
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-100 dark:border-slate-700 pt-4">
              <button
                type="button"
                onClick={() => setSelectedReport(null)}
                className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveModeration}
                disabled={savingMod}
                className="px-5 py-2 text-xs font-semibold bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:opacity-90 transition-opacity shadow-md flex items-center gap-1.5 disabled:opacity-50"
              >
                {savingMod ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
